import { useParams } from 'react-router-dom';
import { MARKET_DOWNLOAD_TIMES, EN_TO_KO_MARKET_NAME_MAP } from './_constants/_consts';
import Lottie from 'lottie-react';
import fireworkAnimation from '@/assets/animation/FireWorks.json';
import CheckMark from '@/assets/icons/V3/CheckMark.svg?react';

import { MarketInfoProps } from '@/types/market';
import { LayoutOrderContainer, LayoutTabSection } from '../orders/_layouts/LayoutOrderContainer';
import { LayoutTabSection as LayoutTab } from '../orders/_layouts/LayoutTabSection';
import { ROUTES } from '@/routes';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { Button } from '@/components/atoms/PcButton';

const getMarketDownloadTimes = (marketKey: string) => {
  return MARKET_DOWNLOAD_TIMES[marketKey] || { downloadMin: 10, downloadAnnualMin: 40 };
};

const getKoreanMarketName = (englishMarketName: string) => {
  return EN_TO_KO_MARKET_NAME_MAP[englishMarketName] || '쿠팡';
};

const PcMarketConnectinoComplete = () => {
  const { englishMarketName } = useParams();

  const marketName = getKoreanMarketName(englishMarketName || '');
  const { downloadMin, downloadAnnualMin } = getMarketDownloadTimes(englishMarketName || '');

  const marketProps: MarketInfoProps = {
    marketName,
    downloadMin,
    downloadAnnualMin,
  };

  const navigate = useNavigate();

  const handleBackward = () => {
    navigate(ROUTES.market);
  };

  return (
    <LayoutOrderContainer className="scrollbar overflow-y-auto bg-gr-50">
      <LayoutTabSection>
        <BackwardSection />
      </LayoutTabSection>

      <div className="mx-[60px] mt-[24px] flex h-fit flex-col items-center rounded-[16px] bg-white py-[36px]">
        <ConnectionAnimate />
        <article className="flex w-full flex-col items-center gap-[24px] pt-[16px] ">
          <TitleSection {...marketProps} englishMarketName={englishMarketName} />
          <ProgressBar englishMarketName={englishMarketName} />
        </article>
        <DescriptionSection {...marketProps} englishMarketName={englishMarketName} />

        <Button onClick={handleBackward} variant="default" className="mt-[30px] h-[44px] w-[300px]">
          마켓 목록
        </Button>
      </div>
    </LayoutOrderContainer>
  );
};

export default PcMarketConnectinoComplete;

export const TitleSection = ({ marketName, englishMarketName }: MarketInfoProps) => {
  const getMarketTitle = (englishMarketName: string | undefined) => {
    switch (englishMarketName) {
      // case 'toss':
      //   return '연동 신청이 완료됐어요!';
      default:
        return '연동이 완료되었어요!';
    }
  };

  const getMarketDescription = (englishMarketName: string | undefined) => {
    switch (englishMarketName) {
      // case 'toss':
      //   return `연동 완료까지 영업일 기준 하루 정도 소요 돼요.\n연동이 완료되면 푸시로 알려드릴게요.`;
      default:
        return `${marketName} 주문 내역을 가져오는데\n최대 30분이 소요될 수 있어요`;
    }
  };

  return (
    <section className="flex flex-col gap-[12px]">
      <h1 className="text-center text-gr-800 Title3S18">{getMarketTitle(englishMarketName)}</h1>
      <p className="whitespace-pre-wrap text-center text-gr-700 Body5M14">
        {getMarketDescription(englishMarketName)}
      </p>
    </section>
  );
};

export const DescriptionSection = ({
  englishMarketName,
}: {
  englishMarketName: string | undefined;
}) => {
  const getDescription = (englishMarketName: string | undefined) => {
    switch (englishMarketName) {
      // case 'toss':
      //   return '연동이 완료되면 30분 내로 최근 일주일 정보를 가져와요.\n최근 1년 정보를 가져오는데 최대 일주일이 소요될 수 있어요.';
      default:
        return '30분 내로 최근 일주일 정보를 가져와요.\n최근 1년 정보를 가져오는데 최대 일주일이 소요될 수 있어요.';
    }
  };
  return (
    <section className="mt-[16px] flex flex-col gap-2">
      <p className=" whitespace-pre-wrap text-center text-gr-700 Body5M14">
        {getDescription(englishMarketName)}
      </p>
    </section>
  );
};

const ConnectionAnimate = () => {
  return (
    <section className="flex">
      <Lottie animationData={fireworkAnimation} className="w-[40px]" loop />
    </section>
  );
};

const BackwardSection = () => {
  const navigate = useNavigate();

  const handleBackward = () => {
    navigate(ROUTES.market);
  };

  return (
    <LayoutTab>
      <div
        onClick={handleBackward}
        className="flex cursor-pointer items-center gap-3 text-gr-900 Title2S24"
      >
        <ChevronLeft className="h-[32px] w-[32px]" />
        뒤로가기
      </div>
    </LayoutTab>
  );
};

const ProgressBar = ({ englishMarketName }: { englishMarketName: string | undefined }) => {
  const getMarketTitle = (englishMarketName: string | undefined) => {
    switch (englishMarketName) {
      // case 'toss':
      //   return '연동 완료 후';
      default:
        return '오늘';
    }
  };
  return (
    <section className="flex h-[148px] w-[335px] flex-col items-center gap-4 rounded-lg bg-gr-50  py-[32px] pb-[24px]">
      <UpperSection />
      <div className="flex w-full justify-between px-[60px]">
        <div className="flex flex-col items-center">
          <p className="text-gr-700 Body2M16">{getMarketTitle(englishMarketName)}</p>
          <p className="text-gr-800 Body2M16">일주일 정보</p>
        </div>
        <div className="flex flex-col items-center">
          <p className="text-gr-700 Body2M16">일주일 이내</p>
          <p className="text-gr-800 Body2M16">1년 정보</p>
        </div>
      </div>
    </section>
  );
};

const UpperSection = () => {
  return (
    <div className="flex w-full items-center gap-4 px-20">
      <div className="flex h-[32px] w-[32px] items-center justify-center rounded-full bg-gr-900  text-white">
        <CheckMark className="w-[24px]" />
      </div>
      {/* Divider */}
      <div className="flex flex-1">
        <div className="h-px w-[75px]  border-b border-dashed border-gr-900" />
      </div>

      <div className="flex h-[32px] w-[32px] items-center justify-center rounded-full bg-[#d9d9d9] text-gr-900 Body2M16">
        2
      </div>
    </div>
  );
};
